/**
 * Session Cache Manager
 * Optimise la persistance des sessions utilisateur avec cache intelligent
 * et gestion automatique de la reconnexion
 */

// Clés de stockage
const CACHE_KEYS = {
  USER_PROFILE: '@allodriver:user_profile',
  AUTH_TOKEN: '@allodriver:auth_token',
  LAST_SYNC: '@allodriver:last_sync',
  SESSION_EXPIRY: '@allodriver:session_expiry',
  USER_PREFERENCES: '@allodriver:user_preferences',
  CACHED_DATA: '@allodriver:cached_data',
};

// Configuration du cache
const CACHE_CONFIG = {
  SESSION_DURATION: 30 * 24 * 60 * 60 * 1000, // 30 jours en millisecondes
  SYNC_INTERVAL: 5 * 60 * 1000, // Sync toutes les 5 minutes
  MAX_OFFLINE_DURATION: 7 * 24 * 60 * 60 * 1000, // 7 jours max hors ligne
  BACKGROUND_REFRESH_INTERVAL: 15 * 60 * 1000, // Refresh en background toutes les 15 min
};

class SessionCacheManager {
  constructor() {
    this.storage = null;
    this.memoryCache = {};
    this.syncTimer = null;
    this.backgroundRefreshTimer = null;
    this.isInitialized = false;
  }

  /**
   * Initialise le gestionnaire de cache avec le storage approprié
   * @param {Object} storage - AsyncStorage pour mobile ou localStorage pour web
   */
  async initialize(storage) {
    if (this.isInitialized) return;
    
    this.storage = storage;
    this.isInitialized = true;

    // Charger les données en cache mémoire au démarrage
    await this.loadMemoryCache();

    // Démarrer le timer de synchronisation automatique
    this.startBackgroundSync();

    console.log('SessionCache: Initialized successfully');
  }

  /**
   * Charge les données critiques en mémoire pour un accès ultra-rapide
   */
  async loadMemoryCache() {
    try {
      const profile = await this.getItem(CACHE_KEYS.USER_PROFILE);
      const token = await this.getItem(CACHE_KEYS.AUTH_TOKEN);
      const lastSync = await this.getItem(CACHE_KEYS.LAST_SYNC);

      if (profile) this.memoryCache.profile = JSON.parse(profile);
      if (token) this.memoryCache.token = token;
      if (lastSync) this.memoryCache.lastSync = parseInt(lastSync);

      console.log('SessionCache: Memory cache loaded', {
        hasProfile: !!profile,
        hasToken: !!token,
        lastSync: lastSync ? new Date(parseInt(lastSync)).toISOString() : 'never'
      });
    } catch (error) {
      console.error('SessionCache: Error loading memory cache', error);
    }
  }

  /**
   * Sauvegarde le profil utilisateur avec timestamp d'expiration étendu
   */
  async saveUserProfile(profile) {
    try {
      const now = Date.now();
      const expiryTime = now + CACHE_CONFIG.SESSION_DURATION;

      // Cache mémoire
      this.memoryCache.profile = profile;
      this.memoryCache.lastSync = now;

      // Cache persistant
      await this.setItem(CACHE_KEYS.USER_PROFILE, JSON.stringify(profile));
      await this.setItem(CACHE_KEYS.LAST_SYNC, now.toString());
      await this.setItem(CACHE_KEYS.SESSION_EXPIRY, expiryTime.toString());

      console.log('SessionCache: User profile saved', {
        uid: profile.uid,
        expiresIn: Math.floor(CACHE_CONFIG.SESSION_DURATION / (24 * 60 * 60 * 1000)) + ' days'
      });

      return true;
    } catch (error) {
      console.error('SessionCache: Error saving user profile', error);
      return false;
    }
  }

  /**
   * Récupère le profil utilisateur (mémoire puis storage)
   */
  async getUserProfile() {
    try {
      // 1. Vérifier le cache mémoire d'abord (ultra-rapide)
      if (this.memoryCache.profile) {
        console.log('SessionCache: Profile retrieved from memory cache');
        return this.memoryCache.profile;
      }

      // 2. Vérifier le storage persistant
      const profileStr = await this.getItem(CACHE_KEYS.USER_PROFILE);
      if (!profileStr) {
        console.log('SessionCache: No cached profile found');
        return null;
      }

      const profile = JSON.parse(profileStr);

      // 3. Vérifier l'expiration
      const expiry = await this.getItem(CACHE_KEYS.SESSION_EXPIRY);
      if (expiry && Date.now() > parseInt(expiry)) {
        console.log('SessionCache: Cached profile expired');
        await this.clearUserSession();
        return null;
      }

      // 4. Recharger en mémoire
      this.memoryCache.profile = profile;
      console.log('SessionCache: Profile retrieved from persistent storage');
      
      return profile;
    } catch (error) {
      console.error('SessionCache: Error getting user profile', error);
      return null;
    }
  }

  /**
   * Sauvegarde le token d'authentification
   */
  async saveAuthToken(token) {
    try {
      this.memoryCache.token = token;
      await this.setItem(CACHE_KEYS.AUTH_TOKEN, token);
      console.log('SessionCache: Auth token saved');
      return true;
    } catch (error) {
      console.error('SessionCache: Error saving auth token', error);
      return false;
    }
  }

  /**
   * Récupère le token d'authentification
   */
  async getAuthToken() {
    try {
      if (this.memoryCache.token) {
        return this.memoryCache.token;
      }

      const token = await this.getItem(CACHE_KEYS.AUTH_TOKEN);
      if (token) {
        this.memoryCache.token = token;
      }
      return token;
    } catch (error) {
      console.error('SessionCache: Error getting auth token', error);
      return null;
    }
  }

  /**
   * Vérifie si la session est toujours valide
   */
  async isSessionValid() {
    try {
      const expiry = await this.getItem(CACHE_KEYS.SESSION_EXPIRY);
      if (!expiry) return false;

      const isValid = Date.now() < parseInt(expiry);
      console.log('SessionCache: Session valid:', isValid);
      
      return isValid;
    } catch (error) {
      console.error('SessionCache: Error checking session validity', error);
      return false;
    }
  }

  /**
   * Prolonge la durée de la session
   */
  async extendSession() {
    try {
      const now = Date.now();
      const newExpiry = now + CACHE_CONFIG.SESSION_DURATION;
      
      await this.setItem(CACHE_KEYS.SESSION_EXPIRY, newExpiry.toString());
      await this.setItem(CACHE_KEYS.LAST_SYNC, now.toString());
      
      this.memoryCache.lastSync = now;
      
      console.log('SessionCache: Session extended by 30 days');
      return true;
    } catch (error) {
      console.error('SessionCache: Error extending session', error);
      return false;
    }
  }

  /**
   * Sauvegarde des données génériques en cache
   */
  async cacheData(key, data, ttl = null) {
    try {
      const cacheEntry = {
        data: data,
        timestamp: Date.now(),
        ttl: ttl || CACHE_CONFIG.SESSION_DURATION
      };

      const cacheKey = `${CACHE_KEYS.CACHED_DATA}:${key}`;
      await this.setItem(cacheKey, JSON.stringify(cacheEntry));
      
      console.log('SessionCache: Data cached', { key, ttl });
      return true;
    } catch (error) {
      console.error('SessionCache: Error caching data', error);
      return false;
    }
  }

  /**
   * Récupère des données en cache
   */
  async getCachedData(key) {
    try {
      const cacheKey = `${CACHE_KEYS.CACHED_DATA}:${key}`;
      const cacheStr = await this.getItem(cacheKey);
      
      if (!cacheStr) return null;

      const cacheEntry = JSON.parse(cacheStr);
      const age = Date.now() - cacheEntry.timestamp;

      // Vérifier si expiré
      if (age > cacheEntry.ttl) {
        await this.removeItem(cacheKey);
        console.log('SessionCache: Cached data expired', { key });
        return null;
      }

      console.log('SessionCache: Cached data retrieved', { key, age: Math.floor(age / 1000) + 's' });
      return cacheEntry.data;
    } catch (error) {
      console.error('SessionCache: Error getting cached data', error);
      return null;
    }
  }

  /**
   * Synchronisation en arrière-plan
   */
  startBackgroundSync() {
    if (this.backgroundRefreshTimer) {
      clearInterval(this.backgroundRefreshTimer);
    }

    this.backgroundRefreshTimer = setInterval(async () => {
      const isValid = await this.isSessionValid();
      if (isValid) {
        // Prolonger automatiquement la session si l'utilisateur est actif
        await this.extendSession();
        console.log('SessionCache: Background sync - session extended');
      }
    }, CACHE_CONFIG.BACKGROUND_REFRESH_INTERVAL);

    console.log('SessionCache: Background sync started');
  }

  /**
   * Arrête la synchronisation en arrière-plan
   */
  stopBackgroundSync() {
    if (this.backgroundRefreshTimer) {
      clearInterval(this.backgroundRefreshTimer);
      this.backgroundRefreshTimer = null;
      console.log('SessionCache: Background sync stopped');
    }
  }

  /**
   * Nettoie la session utilisateur
   */
  async clearUserSession() {
    try {
      await this.removeItem(CACHE_KEYS.USER_PROFILE);
      await this.removeItem(CACHE_KEYS.AUTH_TOKEN);
      await this.removeItem(CACHE_KEYS.SESSION_EXPIRY);
      await this.removeItem(CACHE_KEYS.LAST_SYNC);

      this.memoryCache = {};
      
      console.log('SessionCache: User session cleared');
      return true;
    } catch (error) {
      console.error('SessionCache: Error clearing session', error);
      return false;
    }
  }

  /**
   * Nettoie tout le cache (préférences conservées)
   */
  async clearAllCache() {
    try {
      const preferences = await this.getItem(CACHE_KEYS.USER_PREFERENCES);
      
      // Supprimer toutes les clés de cache
      await Promise.all(
        Object.values(CACHE_KEYS).map(key => this.removeItem(key))
      );

      // Restaurer les préférences
      if (preferences) {
        await this.setItem(CACHE_KEYS.USER_PREFERENCES, preferences);
      }

      this.memoryCache = {};
      
      console.log('SessionCache: All cache cleared (preferences preserved)');
      return true;
    } catch (error) {
      console.error('SessionCache: Error clearing all cache', error);
      return false;
    }
  }

  /**
   * Méthodes auxiliaires pour gérer le storage de manière uniforme
   */
  async setItem(key, value) {
    if (!this.storage) {
      console.warn('SessionCache: Storage not initialized');
      return;
    }

    // AsyncStorage (mobile) ou localStorage (web)
    if (this.storage.setItem) {
      await this.storage.setItem(key, value);
    }
  }

  async getItem(key) {
    if (!this.storage) {
      console.warn('SessionCache: Storage not initialized');
      return null;
    }

    if (this.storage.getItem) {
      // Pour AsyncStorage, la callback est optionnelle
      try {
        return await this.storage.getItem(key);
      } catch (error) {
        console.error('SessionCache: Error in getItem', error);
        return null;
      }
    }
    return null;
  }

  async removeItem(key) {
    if (!this.storage) {
      console.warn('SessionCache: Storage not initialized');
      return;
    }

    if (this.storage.removeItem) {
      await this.storage.removeItem(key);
    }
  }

  /**
   * Statistiques du cache
   */
  async getCacheStats() {
    try {
      const profile = await this.getUserProfile();
      const token = await this.getAuthToken();
      const lastSync = await this.getItem(CACHE_KEYS.LAST_SYNC);
      const expiry = await this.getItem(CACHE_KEYS.SESSION_EXPIRY);

      const stats = {
        hasProfile: !!profile,
        hasToken: !!token,
        lastSync: lastSync ? new Date(parseInt(lastSync)).toISOString() : null,
        sessionExpiry: expiry ? new Date(parseInt(expiry)).toISOString() : null,
        isValid: await this.isSessionValid(),
        memoryCacheSize: Object.keys(this.memoryCache).length,
        sessionDurationDays: Math.floor(CACHE_CONFIG.SESSION_DURATION / (24 * 60 * 60 * 1000))
      };

      return stats;
    } catch (error) {
      console.error('SessionCache: Error getting cache stats', error);
      return null;
    }
  }
}

// Instance singleton
const SessionCache = new SessionCacheManager();

export default SessionCache;
export { CACHE_CONFIG, CACHE_KEYS };
